import React, { useContext, useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { AppContext } from "../../../Context/AppContext";
import styles from "./QuickPeek.module.css";
import Footer from "../../../components/Footer";
import { IdCardLanyard } from "lucide-react";
import { useNavigate } from 'react-router-dom';

const departmentMap = {
    CS: "Computer Science",
    EE: "Electrical Engineering",
    ME: "Mechanical Engineering",
};

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const toDate = (val) => {
    if (!val) return null;
    if (typeof val === "object" && typeof val.toDate === "function") return val.toDate();
    if (typeof val === "number") return new Date(val);
    return new Date(String(val));
};

const isBirthday = (dateString) => {
    if (!dateString) return false;
    const s = String(dateString).trim();
    const parts = s.split("-");
    let d;
    if (parts.length === 3 && parts[0].length === 4) {
        const [y, m, day] = parts.map((p) => Number(p));
        d = new Date(y, m - 1, day);
    } else {
        d = toDate(s);
    }
    if (!d || Number.isNaN(d.getTime())) return false;
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth();
};

const QuickPeek = ({ onNavigateToHomework }) => {
    const { user } = useContext(AppContext);
    const navigate = useNavigate();

    const [nearestHomework, setNearestHomework] = useState(null);
    const [todaysLectures, setTodaysLectures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isBirthdayToday, setIsBirthdayToday] = useState(false);
    const [hasIdCard, setHasIdCard] = useState(false);

    // Check for eID card
    useEffect(() => {
        const checkIdCard = async () => {
            if (!user?.stdRegNumber) return;
            try {
                const idCardRef = doc(db, 'idCards', user.stdRegNumber);
                const idCardDoc = await getDoc(idCardRef);
                setHasIdCard(idCardDoc.exists());
            } catch (error) {
                console.error('Error checking ID card:', error);
            }
        };
        checkIdCard();
    }, [user]);

    // Birthday check
    useEffect(() => {
        if (!user) return;
        const dob = user.dateOfBirth || user.dob || user.date || null;
        setIsBirthdayToday(isBirthday(dob));
    }, [user]);

    // Fetch Homework Spotlight
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        setLoading(true);
        setError("");
        setNearestHomework(null);

        const programVal = user.program ? user.program.trim() : null;
        const rawDept = user.department ? user.department.trim() : null;
        const mappedDept = rawDept ? (departmentMap[rawDept] || rawDept) : null;
        const semesterVal =
            user.semester !== undefined && user.semester !== null
                ? Number(String(user.semester).trim())
                : null;
        const sectionVal = user.section ? String(user.section).trim() : null;

        const fetchHomeworks = async () => {
            try {
                const filters = [];
                if (programVal) filters.push(where("program", "==", programVal));
                if (mappedDept) filters.push(where("department", "==", mappedDept));
                if (!Number.isNaN(semesterVal) && semesterVal !== null) {
                    filters.push(where("semester", "==", semesterVal));
                }
                if (sectionVal) filters.push(where("section", "==", sectionVal));

                const q = filters.length
                    ? query(collection(db, "homeworks"), ...filters)
                    : collection(db, "homeworks");

                const snap = await getDocs(q);
                const results = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }));

                const validSorted = results
                    .map((item) => {
                        const dl = toDate(item.deadline);
                        return {
                            ...item,
                            __deadlineDate: dl instanceof Date && !isNaN(dl) ? dl : null,
                        };
                    })
                    .filter((i) => i.__deadlineDate)
                    .sort((a, b) => a.__deadlineDate - b.__deadlineDate);

                if (!cancelled) {
                    setNearestHomework(validSorted[0] || null);
                }
            } catch (err) {
                console.error("Error fetching nearest homework:", err);
                if (!cancelled) setError("Failed to fetch spotlight. Try again later.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchHomeworks();
        return () => {
            cancelled = true;
        };
    }, [user]);

    // Fetch Today's Lectures
    useEffect(() => {
        if (!user?.department || !user?.program || !user?.semester || !user?.section) return;

        const fetchTodaysLectures = async () => {
            try {
                const todayName = DAYS[new Date().getDay()]; // e.g. "Monday"
                const docId = `${user.department}_${user.program}_${user.semester}_${user.section}`;
                const docRef = doc(db, "timetables", docId);
                const snapshot = await getDoc(docRef);

                if (snapshot.exists()) {
                    const data = snapshot.data();
                    if (data.slots?.length > 0) {
                        const lectures = data.slots
                            .map((slot) => {
                                const info = slot.days?.[todayName];
                                if (info?.subject) {
                                    return {
                                        slot: slot.label,
                                        subject: info.subject,
                                        teacher: info.teacher || "—",
                                        room: info.room || "—",
                                    };
                                }
                                return null;
                            })
                            .filter(Boolean);

                        setTodaysLectures(lectures);
                    }
                }
            } catch (err) {
                console.error("Error fetching today's lectures:", err);
            }
        };

        fetchTodaysLectures();
    }, [user]);

    const formatDeadline = (dateVal) => {
        const date = toDate(dateVal);
        if (!date || Number.isNaN(date.getTime())) return "—";
        const today = new Date();
        const tomorrow = new Date();
        const yesterday = new Date();

        today.setHours(0, 0, 0, 0);
        tomorrow.setDate(today.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        yesterday.setDate(today.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);

        const d = new Date(date);
        d.setHours(0, 0, 0, 0);

        if (d.getTime() === today.getTime()) return "Today";
        if (d.getTime() === tomorrow.getTime()) return "Tomorrow";
        if (d.getTime() === yesterday.getTime()) return "Yesterday";

        return `${d.getDate()} ${d.toLocaleString("en-US", {
            month: "long",
        })} ${d.getFullYear()}`;
    };

    const displayName = (user?.firstName || user?.username || "Student").trim();

    const handleIdCardClick = () => {
        if (hasIdCard) {
            navigate(`/student-id-card/${user.stdRegNumber}`);
        } else {
            navigate('/get-id-card-prompt');
        }
    };

    return (
        <div className={styles.container}>
            <div style={{ position: 'absolute', top: '1rem', left: '1rem' }} className={styles.idCardCont}>
                <button onClick={handleIdCardClick} className={styles.idCardButton}>
                    <IdCardLanyard size={24} />
                    {/*View ID Card*/}
                </button>
            </div>
            <br></br>
            <h2 className={styles.welcome}>Hi {displayName} 👋, welcome back!</h2>

            {isBirthdayToday && (
                <div className={styles.birthdayBox}>
                    🎉 Happy Birthday, {displayName}! 🎂
                </div>
            )}

            <div className={styles.section}>

                {/* Homework Spotlight */}
                <div className={styles.sectionBox}>
                    <div className={styles.sectionHeader}>
                        <h3> Homework Spotlight</h3>
                    </div>

                    {loading && (
                        <p className={styles.message}>Loading your spotlight...</p>
                    )}
                    {error && <p className={styles.messageError}>{error}</p>}
                    {!loading && !nearestHomework && !error && (
                        <p className={styles.emptyState}>✅ No homework due for your class right now!</p>
                    )}

                    {!loading && nearestHomework && (
                        <div
                            className={styles.homeworkCard}
                            onClick={() => onNavigateToHomework?.()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onNavigateToHomework?.();
                            }}
                        >
                            <h4 className={styles.homeworkTitle}>
                                {String(nearestHomework.title || "Untitled").trim()}
                            </h4>
                            <p className={styles.deadline}>
                                Deadline: {formatDeadline(nearestHomework.deadline)}
                            </p>
                            <p className={styles.clickHint}>
                                Click to view all homeworks →
                            </p>
                        </div>
                    )}
                </div>

                {/* Today's Lectures */}
                <div className={styles.sectionBox}>
                    <div className={styles.sectionHeader}>
                        <h3> Today's Lectures</h3>
                    </div>

                    {todaysLectures.length === 0 ? (
                        <p className={styles.emptyState}>
                            🎉 Relax! No lectures scheduled for today.
                        </p>
                    ) : (
                        <div className={styles.lecturesList}>
                            {todaysLectures.map((lec, idx) => (
                                <div key={idx} className={styles.lectureCard}>
                                    <h4 className={styles.subject}>{lec.subject}</h4>
                                    <p><strong>Slot:</strong> {lec.slot}</p>
                                    <p><strong>Teacher:</strong> {lec.teacher}</p>
                                    <p><strong>Room:</strong> {lec.room}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
            <Footer />

        </div>
    );
};

export default QuickPeek;
